import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import { AppError, EmptyState, Loader } from "@bfc-software/pallet";
import {
  TRANSLATION_NAMESPACE,
  useTranslation,
} from "@bfc-software/bfc-web-ui/i18n";
import { rawFetchClient } from "@bfc-software/bfc-web-ui/lib/analytics-api-client";
import { mswActive } from "@bfc-software/bfc-web-ui/lib/msw";
import { AppHeader } from "@bfc-software/bfc-web-ui/ui/shared/app-header";
import { PresetDashboard } from "@bfc-software/bfc-web-ui/ui/analytics/preset-dashboard";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useContext, useEffect, useState } from "react";

import styles from "./analytics.module.css";

/**
 * PowerAnalytics. This page embeds a Preset dashboard in an iframe.
 *
 * The scope is the organization, and it holds no warehouse. Sign-in mints a
 * session with no warehouse, so the AAA token carries `oid` but neither `whsid`
 * nor `aud`. AuthzExchangeToken in access_authzn sets those two claims only if the
 * caller gives a warehouse. The analytics service resolves the permitted rows from
 * `oid` alone.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/analytics",
)({
  component: RouteComponent,
});

/**
 * Under MSW the page renders a stub instead of embedding. MSW cannot intercept the
 * requests a cross-origin iframe makes, so a real embed would reach Preset.
 */
const STUB_EMBED = mswActive();

/** What the guest-token response tells us to embed. */
type EmbedConfig = { supersetDomain: string; dashboardId: string };

type Fatal = "unauthorized" | "not-configured" | "failed";

export function RouteComponent() {
  const { lang, org } = Route.useParams();
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.analytics,
  });
  const { state: authState } = useContext(AuthContext);

  // Null until the auth machine loads the user's organizations, so this doubles as
  // the "auth is ready" gate. Do not add a `?? org` fallback. The URL slug is
  // always present, so the page would request a token before an aaaToken exists.
  const orgId = AuthUtils.getOrgIdByStub(authState, org);
  const hydrated = Boolean(orgId);

  const [config, setConfig] = useState<EmbedConfig | null>(null);
  const [fatal, setFatal] = useState<Fatal | null>(null);

  /**
   * The SDK's guest-token callback. The SDK decodes `exp` and calls this again
   * before the token expires, so do not convert it to a query. A second scheduler
   * can serve a cached guest token after that token expires.
   *
   * The dependency list is empty, so the identity never changes and the embed
   * effect never re-runs because of it.
   */
  const fetchGuestToken = useCallback(async (): Promise<string> => {
    // openapi-fetch returns `{ error }` for an HTTP error, but it throws for a
    // transport failure. Client construction is inside the try for that reason. A
    // throw that set no fatal state would leave the page on its loader forever.
    try {
      const client = rawFetchClient();
      const { data, error, response } = await client.POST("/guest-token", {});

      if (error || !data?.data?.token) {
        const status = response?.status;
        // The SDK does not retry. refreshGuestToken awaits this callback and
        // re-arms its timer only after a resolve, so a rejection ends the refresh
        // schedule silently. A fatal state is the only thing that surfaces it. Any
        // long-open tab reaches this branch, because nothing re-mints the aaaToken.
        if (status === 401 || status === 403) {
          setFatal("unauthorized");
        } else if (status === 404) {
          setFatal("not-configured");
        } else {
          setFatal("failed");
        }
        // Never log the token. The logger writes to the console and to Sentry.
        logger.warn(`Guest token request failed with status ${String(status)}`);
        throw new Error(
          `guest token request failed: ${String(status ?? "network")}`,
        );
      }

      setConfig({
        supersetDomain: data.data.superset_domain,
        dashboardId: data.data.dashboard_id,
      });
      return data.data.token;
    } catch (caught) {
      // Rethrown because the SDK expects a rejected promise. The catch guarantees
      // a fatal state first, so the page shows an error and not a loader forever.
      // `current ?? "failed"` keeps a specific state the branch above already set.
      setFatal((current) => current ?? "failed");
      logger.warn(`Guest token request threw: ${String(caught)}`);
      throw caught;
    }
  }, []);

  // `current ?? "failed"` is reachable here. The SDK fetches its own token, so a
  // 403 on that call sets "unauthorized" and then rejects embedDashboard in the
  // same microtask chain, before React commits the unmount. An unconditional
  // "failed" would relabel a permissions problem as a generic one.
  const handleFailure = useCallback(
    () => setFatal((current) => current ?? "failed"),
    [],
  );

  // Primes the page so it knows which dashboard and domain to embed.
  // embedDashboard fetches its own token and does not accept this one, so each
  // page load mints two. Do not cache to collapse them. A guest token served twice
  // after it expires is the failure this callback exists to prevent.
  useEffect(() => {
    if (!hydrated || config || fatal) {
      return;
    }
    void fetchGuestToken().catch(() => {
      // fetchGuestToken has already set the fatal state and logged.
    });
  }, [hydrated, config, fatal, fetchGuestToken]);

  const body = () => {
    if (fatal === "not-configured") {
      return (
        <EmptyState heading={t("notConfigured")}>
          {t("notConfiguredHelp")}
        </EmptyState>
      );
    }
    if (fatal === "unauthorized") {
      return (
        <AppError
          description={t("unauthorized")}
          resolution={t("unauthorizedHelp")}
        />
      );
    }
    if (fatal === "failed") {
      return (
        <AppError description={t("failed")} resolution={t("failedHelp")} />
      );
    }
    if (!hydrated || !config) {
      return <Loader type="page" />;
    }

    return (
      <PresetDashboard
        // The RLS clause is baked into the token, so a scope change must remount
        // rather than reuse a frame holding the old scope.
        key={orgId ?? ""}
        dashboardId={config.dashboardId}
        supersetDomain={config.supersetDomain}
        fetchGuestToken={fetchGuestToken}
        iframeTitle={t("iframeTitle")}
        stub={
          STUB_EMBED ? (
            <EmptyState heading={t("stubHeading")}>
              <span data-test-role="preset-embed-stub">
                {t("stubHelp")} {config.dashboardId}
              </span>
            </EmptyState>
          ) : undefined
        }
        onFailure={handleFailure}
      />
    );
  };

  return (
    <>
      <AppHeader />
      <div className={styles.page} data-test-role="analytics-page">
        <div className={styles.embedArea}>{body()}</div>
      </div>
    </>
  );
}
